import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, User, Building, Camera, Palette, Sparkles, ArrowRight, Info } from 'lucide-react';
import { services as mockServices } from '../data/mockData';
import { publicAPI } from '../services/apiService';
import ServiceDetailsModal from '../components/ServiceDetailsModal';

const Services = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await publicAPI.getServices();
      const apiServices = response.data.services || [];
      
      // Use API services if available, fallback to mock data
      if (apiServices.length > 0) {
        setServices(apiServices);
      } else {
        setServices(mockServices);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      // Fallback to mock data on error
      setServices(mockServices);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceDetails = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleBookNow = () => {
    setIsModalOpen(false);
    navigate('/booking');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const iconMap = {
    Heart,
    User,
    Building,
    Camera,
    Palette,
    Sparkles
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1610680143139-8f15ac90b4ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdoaXRlJTIwcGhvdG9ncmFwaHl8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzU3NTA3MzU5fDA&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('services.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-gray-400">
              <p>Loading services...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.filter(service => service.active !== false).map((service) => {
              const IconComponent = iconMap[service.icon] || Camera; // Default icon for API services
              return (
                <div 
                  key={service.id} 
                  className="group bg-gray-900 p-8 border border-gray-800 hover:border-white transition-all duration-500 hover:transform hover:scale-105"
                >
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-white text-black flex items-center justify-center group-hover:bg-gray-200 transition-colors duration-300">
                        <IconComponent className="h-8 w-8" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 
                        className="text-2xl font-bold mb-4 group-hover:text-gray-300 transition-colors duration-300"
                        style={{ fontFamily: '"Times New Roman", serif' }}
                      >
                        {service[`name_${currentLang}`]}
                      </h3>
                      <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                        {service[`description_${currentLang}`]}
                      </p>
                      <div className="flex items-center justify-between">
                        <span 
                          className="text-xl font-semibold"
                          style={{ fontFamily: '"Times New Roman", serif' }}
                        >
                          {service.price}
                        </span>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleServiceDetails(service)}
                            className="inline-flex items-center text-gray-300 hover:text-white transition-colors duration-300"
                          >
                            <Info className="h-4 w-4 mr-1" />
                            {t('services.details.title')}
                          </button>
                          <Link 
                            to="/booking"
                            className="inline-flex items-center text-white hover:text-gray-300 transition-colors duration-300"
                          >
                            {t('nav.booking')}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-16 text-center">
            <div className="bg-gray-900 p-8 border border-gray-800 max-w-4xl mx-auto">
              <h3 
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('services.customPackages.title')}
              </h3>
              <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                {t('services.customPackages.description')}
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                {t('services.customPackages.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('services.process.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('services.process.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                titleKey: 'services.process.steps.consultation.title',
                descriptionKey: 'services.process.steps.consultation.description'
              },
              {
                step: '02',
                titleKey: 'services.process.steps.planning.title',
                descriptionKey: 'services.process.steps.planning.description'
              },
              {
                step: '03',
                titleKey: 'services.process.steps.shooting.title',
                descriptionKey: 'services.process.steps.shooting.description'
              },
              {
                step: '04',
                titleKey: 'services.process.steps.editing.title',
                descriptionKey: 'services.process.steps.editing.description'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-4xl font-bold mb-4 text-gray-600"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {process.step}
                </div>
                <h4 
                  className="text-xl font-semibold mb-3"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t(process.titleKey)}
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {t(process.descriptionKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Details Modal */}
      <ServiceDetailsModal 
        service={selectedService}
        isOpen={isModalOpen}
        onClose={closeModal}
        onBookNow={handleBookNow}
      />
    </div>
  );
};

export default Services;